import { useEffect, useState } from "react";
import { Trophy, Medal, EyeOff } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/States";
import { api, formatError } from "@/lib/api";

const MEDAL = ["text-amber-500", "text-ink-3", "text-orange-700"];
const fmt = (d) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" });

const Row = ({ e }) => (
  <div data-testid={`leaderboard-row-${e.rank}`} className={`flex items-center gap-4 px-5 py-4 text-sm ${e.is_me ? "bg-aqua-soft" : ""}`}>
    <span className="flex w-10 items-center justify-center gap-0.5 font-display text-lg font-extrabold text-ink">
      {e.rank <= 3 && <Medal className={`h-5 w-5 ${MEDAL[e.rank - 1]}`} />}{e.rank}
    </span>
    <span className="flex-1 font-semibold text-ink">{e.name}{e.is_me && <span className="ml-2 rounded-full bg-aqua-solid px-2 py-0.5 text-[10px] font-semibold uppercase text-white">You</span>}</span>
    <span className="hidden w-24 text-right font-mono text-xs text-ink-3 sm:block">{e.days}</span>
    <span className="w-16 text-right font-mono text-xs text-ink-3">{e.accuracy}%</span>
    <span className="w-16 text-right font-mono font-bold text-ink">{e.score}</span>
  </div>
);

export default function Leaderboard() {
  const [week, setWeek] = useState("current");
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { setD(null); api.get(`/leaderboard?week=${week}`).then((r) => setD(r.data)).catch((e) => setErr(formatError(e))); }, [week]);

  return (
    <div>
      <PageHeader eyebrow="Weekly leaderboard" title="Top daily quiz scorers" text="Ranked by correct answers in daily quizzes this week (Monday–Sunday, UTC). Ties are broken by accuracy." >
        <div className="inline-flex rounded-lg border border-line bg-surface p-1">
          {[["current", "This week"], ["last", "Last week"]].map(([k, l]) => (
            <button key={k} data-testid={`leaderboard-week-${k}`} onClick={() => setWeek(k)}
              className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${week === k ? "bg-solid text-white" : "text-ink-2 hover:text-ink"}`}>{l}</button>
          ))}
        </div>
      </PageHeader>
      {err ? <ErrorState message={err} /> : !d ? <Loading /> : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <p className="mb-3 text-xs text-ink-3">{fmt(d.week_start)} – {fmt(d.week_end)} · {d.participants} participant{d.participants === 1 ? "" : "s"}</p>
            {d.entries.length === 0 ? <EmptyState testId="leaderboard-empty" title="No scores yet this week" text="Complete today's daily quiz to claim the top spot." /> : (
              <div data-testid="leaderboard-list" className="divide-y divide-line overflow-hidden rounded-2xl border border-line bg-surface">
                <div className="flex items-center gap-4 bg-canvas-2 px-5 py-2 text-[11px] font-semibold uppercase tracking-wider text-ink-3">
                  <span className="w-10 text-center">#</span><span className="flex-1">Learner</span><span className="hidden w-24 text-right sm:block">Daily quizzes</span><span className="w-16 text-right">Acc.</span><span className="w-16 text-right">Score</span>
                </div>
                {d.entries.map((e) => <Row key={e.rank} e={e} />)}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-4">
            <div data-testid="leaderboard-me" className="rounded-2xl bg-band p-6 text-white">
              <Trophy className="h-7 w-7 text-[var(--brand-cyan)]" />
              {d.hidden ? (
                <p className="mt-3 text-sm text-slate-300"><EyeOff className="mr-1 inline h-4 w-4" />You're hidden from the leaderboard. Turn it back on in your <Link to="/profile" className="underline">Profile</Link>.</p>
              ) : d.me ? (
                <>
                  <p className="mt-3 font-display text-4xl font-extrabold">#{d.me.rank}</p>
                  <p className="text-sm text-slate-300">{d.me.score} correct · {d.me.accuracy}% accuracy · {d.me.days} daily quiz{d.me.days === 1 ? "" : "zes"}</p>
                </>
              ) : (
                <p className="mt-3 text-sm text-slate-300">You haven't taken a daily quiz {week === "current" ? "this" : "last"} week yet.</p>
              )}
              {week === "current" && !d.daily_done_today && <Link to="/daily" data-testid="leaderboard-daily-link" className="mt-4 inline-block rounded-full bg-aqua-solid px-4 py-2 text-sm font-semibold hover:bg-aqua-solid-hover">Take today's daily quiz</Link>}
              {week === "current" && d.daily_done_today && <p data-testid="leaderboard-daily-done" className="mt-4 text-xs text-[var(--brand-cyan)]">Today's daily quiz is done — come back tomorrow to climb higher.</p>}
            </div>
            <p className="text-xs text-ink-3">Only first names and last initials are shown. You can hide yourself from the leaderboard on your Profile page.</p>
          </div>
        </div>
      )}
    </div>
  );
}
